/**
 * ================================================================
 * FCM TOKEN MANAGEMENT SERVICE
 * ================================================================
 *
 * @fileOverview     Firebase Cloud Messaging token management
 * @module           lib/firebase/fcmTokenManager
 * @version          1.0.0
 * @since            2025-08-11
 * @author           Developer <dev@domain.com>
 *
 * @purpose          Manage FCM tokens for push notifications
 * @dependencies     Firebase messaging, session storage
 * @exports          Token generation, refresh, and storage functions
 *
 * @architecture
 * - Token lifecycle: Generate, store, refresh, and cleanup
 * - Storage: Session storage for token persistence
 * - Error handling: Graceful fallbacks and user-friendly messages
 * - Permissions: Request and handle notification permissions
 *
 * @security
 * - Token validation: Verify token format and validity
 * - Permissions: Respect user notification preferences
 * - Storage: Secure token storage in session storage
 *
 * ================================================================
 */

import { getToken, onMessage } from 'firebase/messaging';

import type {
  FCMTokenInfo,
  FCMTokenResult,
  PermissionAttemptInfo,
} from '@/types/firebase';

import { getFirebaseMessaging, isFirebaseConfigured } from './config';

// ================================================================
// STORAGE KEYS AND CONSTANTS
// ================================================================

const FCM_TOKEN_KEY = 'fcm_token';
const FCM_TOKEN_TIMESTAMP_KEY = 'fcm_token_timestamp';
const TOKEN_REFRESH_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours

// ================================================================
// FCM RETRY CONFIGURATION AND TRACKING KEYS
// ================================================================

const FCM_RETRY_CONFIG = {
  maxRetryAttempts: 3,
  cooldownPeriod: 5 * 60 * 1000, // 5 minutes
  sessionResetInterval: 24 * 60 * 60 * 1000, // 24 hours
} as const;

const PERMISSION_ATTEMPT_COUNT_KEY = 'fcm_permission_attempts';
const PERMISSION_LAST_ATTEMPT_KEY = 'fcm_permission_last_attempt';
const PERMISSION_DENIED_FLAG_KEY = 'fcm_permission_denied';
const PERMISSION_SESSION_START_KEY = 'fcm_permission_session_start';

// ================================================================
// PERMISSION ATTEMPT TRACKING FUNCTIONS
// ================================================================

/**
 * Get current permission attempt information from session storage
 *
 * @returns {PermissionAttemptInfo} Current permission attempt data
 */
function getPermissionAttemptInfo(): PermissionAttemptInfo {
  try {
    const attemptCount = parseInt(sessionStorage.getItem(PERMISSION_ATTEMPT_COUNT_KEY) || '0', 10);
    const lastAttemptTimestamp = parseInt(
      sessionStorage.getItem(PERMISSION_LAST_ATTEMPT_KEY) || '0',
      10,
    );
    const permissionDenied = sessionStorage.getItem(PERMISSION_DENIED_FLAG_KEY) === 'true';
    const sessionStartTime = parseInt(
      sessionStorage.getItem(PERMISSION_SESSION_START_KEY) || Date.now().toString(),
      10,
    );

    return {
      attemptCount,
      lastAttemptTimestamp,
      permissionDenied,
      sessionStartTime,
    };
  } catch (error) {
    console.error('🔔 Error reading permission attempt info:', error);
    return {
      attemptCount: 0,
      lastAttemptTimestamp: 0,
      permissionDenied: false,
      sessionStartTime: Date.now(),
    };
  }
}

/**
 * Update permission attempt tracking information
 *
 * @param {Partial<PermissionAttemptInfo>} updates - Updates to apply
 * @returns {void}
 */
function updatePermissionAttemptInfo(updates: Partial<PermissionAttemptInfo>): void {
  try {
    const current = getPermissionAttemptInfo();
    const updated = { ...current, ...updates };

    sessionStorage.setItem(PERMISSION_ATTEMPT_COUNT_KEY, updated.attemptCount.toString());
    sessionStorage.setItem(PERMISSION_LAST_ATTEMPT_KEY, updated.lastAttemptTimestamp.toString());
    sessionStorage.setItem(PERMISSION_DENIED_FLAG_KEY, updated.permissionDenied.toString());
    sessionStorage.setItem(PERMISSION_SESSION_START_KEY, updated.sessionStartTime.toString());
  } catch (error) {
    console.error('🔔 Error updating permission attempt info:', error);
  }
}

/**
 * Check if permission request should be skipped due to retry limits or cooldown
 *
 * @returns {object} Status object with skip decision and reason
 */
function shouldSkipPermissionRequest(): {
  shouldSkip: boolean;
  reason?: string;
  retryAttempts: number;
  cooldownActive: boolean;
} {
  const attemptInfo = getPermissionAttemptInfo();
  const currentTime = Date.now();

  // Reset session if it's been too long
  if (currentTime - attemptInfo.sessionStartTime > FCM_RETRY_CONFIG.sessionResetInterval) {
    console.log('🔄 Resetting FCM permission session after 24 hours');
    clearPermissionAttemptInfo();
    return {
      shouldSkip: false,
      retryAttempts: 0,
      cooldownActive: false,
    };
  }

  // Check if user permanently denied permission
  if (
    attemptInfo.permissionDenied &&
    attemptInfo.attemptCount >= FCM_RETRY_CONFIG.maxRetryAttempts
  ) {
    return {
      shouldSkip: true,
      reason: 'Maximum retry attempts exceeded - permission denied',
      retryAttempts: attemptInfo.attemptCount,
      cooldownActive: false,
    };
  }

  // Check if we're in cooldown period
  const timeSinceLastAttempt = currentTime - attemptInfo.lastAttemptTimestamp;
  if (attemptInfo.attemptCount > 0 && timeSinceLastAttempt < FCM_RETRY_CONFIG.cooldownPeriod) {
    const remainingCooldown = FCM_RETRY_CONFIG.cooldownPeriod - timeSinceLastAttempt;
    return {
      shouldSkip: true,
      reason: `Cooldown active - ${Math.ceil(remainingCooldown / 60000)} minutes remaining`,
      retryAttempts: attemptInfo.attemptCount,
      cooldownActive: true,
    };
  }

  // Check if we've exceeded max attempts
  if (attemptInfo.attemptCount >= FCM_RETRY_CONFIG.maxRetryAttempts) {
    return {
      shouldSkip: true,
      reason: 'Maximum retry attempts exceeded',
      retryAttempts: attemptInfo.attemptCount,
      cooldownActive: false,
    };
  }

  return {
    shouldSkip: false,
    retryAttempts: attemptInfo.attemptCount,
    cooldownActive: false,
  };
}

/**
 * Clear permission attempt tracking information
 *
 * @returns {void}
 */
function clearPermissionAttemptInfo(): void {
  try {
    sessionStorage.removeItem(PERMISSION_ATTEMPT_COUNT_KEY);
    sessionStorage.removeItem(PERMISSION_LAST_ATTEMPT_KEY);
    sessionStorage.removeItem(PERMISSION_DENIED_FLAG_KEY);
    sessionStorage.removeItem(PERMISSION_SESSION_START_KEY);
    console.log('🗑️ Permission attempt tracking cleared');
  } catch (error) {
    console.error('🗑️ Error clearing permission attempt info:', error);
  }
}

/**
 * Reset permission retry count (for manual retry)
 *
 * @returns {void}
 */
export function resetPermissionRetryCount(): void {
  console.log('🔄 Manually resetting FCM permission retry count');
  clearPermissionAttemptInfo();
}

/**
 * Check if FCM permission retry limits have been exceeded
 *
 * @returns {object} Retry status information
 */
export function getRetryStatus(): {
  hasExceededLimit: boolean;
  retryAttempts: number;
  cooldownActive: boolean;
  cooldownRemainingMinutes?: number;
} {
  const skipResult = shouldSkipPermissionRequest();

  let cooldownRemainingMinutes: number | undefined;
  if (skipResult.cooldownActive) {
    const attemptInfo = getPermissionAttemptInfo();
    const timeSinceLastAttempt = Date.now() - attemptInfo.lastAttemptTimestamp;
    const remainingTime = FCM_RETRY_CONFIG.cooldownPeriod - timeSinceLastAttempt;
    cooldownRemainingMinutes = Math.ceil(remainingTime / 60000);
  }

  return {
    hasExceededLimit: skipResult.retryAttempts >= FCM_RETRY_CONFIG.maxRetryAttempts,
    retryAttempts: skipResult.retryAttempts,
    cooldownActive: skipResult.cooldownActive,
    cooldownRemainingMinutes,
  };
}

// ================================================================
// PERMISSION MANAGEMENT
// ================================================================

/**
 * Request notification permission from user with retry limits
 *
 * @returns {Promise<NotificationPermission>} User's permission choice
 * @async
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  try {
    // ================================================================
    // RETRY LIMIT CHECK
    // ================================================================
    //
    // Purpose: Check if we should skip permission request due to limits
    // UX: Prevent annoying users with repeated popups
    // Implementation: Track attempts across sessions
    // ================================================================

    const skipResult = shouldSkipPermissionRequest();
    if (skipResult.shouldSkip) {
      console.warn('🚫 Skipping permission request:', skipResult.reason);
      console.log('🔢 Current retry attempts:', skipResult.retryAttempts);
      return 'denied';
    }

    // ================================================================
    // BROWSER SUPPORT CHECK
    // ================================================================
    //
    // Purpose: Verify browser supports notifications
    // Fallback: Return denied for unsupported browsers
    // Logging: Clear feedback for debugging
    // ================================================================

    if (!('Notification' in window)) {
      console.warn('🔔 This browser does not support notifications');
      updatePermissionAttemptInfo({
        permissionDenied: true,
        lastAttemptTimestamp: Date.now(),
      });
      return 'denied';
    }

    // ================================================================
    // EXISTING PERMISSION CHECK
    // ================================================================
    //
    // Purpose: Return existing permission without making new requests
    // Performance: Avoid unnecessary permission prompts
    // UX: Respect user's previous decisions
    // ================================================================

    if (Notification.permission === 'granted') {
      console.log('🔔 Notification permission already granted');
      // Clear any previous denial flags
      clearPermissionAttemptInfo();
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      console.warn('🔔 Notification permission permanently denied by browser');
      updatePermissionAttemptInfo({
        permissionDenied: true,
        lastAttemptTimestamp: Date.now(),
      });
      return 'denied';
    }

    // ================================================================
    // PERMISSION REQUEST WITH RETRY TRACKING
    // ================================================================
    //
    // Purpose: Request permission and track the attempt
    // Retry logic: Increment attempt count and timestamp
    // UX: Provide clear feedback on permission status
    // ================================================================

    console.log(
      `🔔 Requesting notification permission (attempt ${skipResult.retryAttempts + 1}/${FCM_RETRY_CONFIG.maxRetryAttempts})`,
    );

    // Update attempt tracking before making the request
    const currentAttempt = skipResult.retryAttempts + 1;
    updatePermissionAttemptInfo({
      attemptCount: currentAttempt,
      lastAttemptTimestamp: Date.now(),
    });

    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      console.log('✅ Notification permission granted');
      // Clear tracking on successful grant
      clearPermissionAttemptInfo();
    } else {
      console.warn(
        `❌ Notification permission denied (attempt ${currentAttempt}/${FCM_RETRY_CONFIG.maxRetryAttempts})`,
      );
      updatePermissionAttemptInfo({
        permissionDenied: true,
      });

      if (currentAttempt >= FCM_RETRY_CONFIG.maxRetryAttempts) {
        console.warn(
          '🚫 Maximum FCM permission retry attempts reached. Will not request again this session.',
        );
      }
    }

    return permission;
  } catch (error) {
    console.error('🔔 Error requesting notification permission:', error);
    return 'denied';
  }
}

// ================================================================
// TOKEN MANAGEMENT
// ================================================================

/**
 * Generate new FCM token with retry limits
 *
 * @returns {Promise<FCMTokenResult>} Token generation result with retry info
 * @async
 */
export async function generateFCMToken(): Promise<FCMTokenResult> {
  try {
    // ================================================================
    // RETRY LIMIT CHECK
    // ================================================================
    //
    // Purpose: Check if we should skip token generation due to retry limits
    // UX: Prevent infinite loops and resource waste
    // Performance: Avoid unnecessary Firebase operations
    // ================================================================

    const skipResult = shouldSkipPermissionRequest();
    const attemptInfo = getPermissionAttemptInfo();

    if (skipResult.shouldSkip) {
      console.warn('🚫 Skipping FCM token generation:', skipResult.reason);
      return {
        success: false,
        error: skipResult.reason || 'Retry limit exceeded',
        retryAttempts: skipResult.retryAttempts,
        retryLimitExceeded: skipResult.retryAttempts >= FCM_RETRY_CONFIG.maxRetryAttempts,
      };
    }

    // ================================================================
    // PREREQUISITES CHECK
    // ================================================================
    //
    // Purpose: Verify Firebase configuration and browser support
    // Safety: Prevent token generation in unsupported environments
    // UX: Provide clear feedback on why token generation failed
    // ================================================================

    if (!isFirebaseConfigured()) {
      return {
        success: false,
        error: 'Firebase not configured',
        retryAttempts: attemptInfo.attemptCount,
      };
    }

    // ================================================================
    // PERMISSION REQUEST WITH RETRY TRACKING
    // ================================================================
    //
    // Purpose: Request permission with retry limits already applied
    // Integration: Use updated requestNotificationPermission function
    // Fallback: Return failure with retry information
    // ================================================================

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      const updatedAttemptInfo = getPermissionAttemptInfo();
      return {
        success: false,
        error: 'Notification permission not granted',
        permissionGranted: false,
        retryAttempts: updatedAttemptInfo.attemptCount,
        retryLimitExceeded: updatedAttemptInfo.attemptCount >= FCM_RETRY_CONFIG.maxRetryAttempts,
      };
    }

    // ================================================================
    // TOKEN GENERATION
    // ================================================================
    //
    // Purpose: Generate FCM token for this browser/device
    // VAPID: Use public VAPID key from environment
    // Error handling: Detailed error logging for debugging
    // ================================================================

    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      return {
        success: false,
        error: 'Firebase messaging not available',
        retryAttempts: attemptInfo.attemptCount,
      };
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn('🔑 VAPID key not configured, using default Firebase configuration');
    }

    const tokenOptions = vapidKey ? { vapidKey } : {};
    const token = await getToken(messaging, tokenOptions);

    if (!token) {
      return {
        success: false,
        error: 'Failed to generate FCM token',
        retryAttempts: attemptInfo.attemptCount,
      };
    }

    // ================================================================
    // TOKEN STORAGE
    // ================================================================
    //
    // Purpose: Store token with timestamp for refresh logic
    // Storage: Session storage for security and session binding
    // Metadata: Include timestamp for token age validation
    // ================================================================

    const timestamp = Date.now();
    sessionStorage.setItem(FCM_TOKEN_KEY, token);
    sessionStorage.setItem(FCM_TOKEN_TIMESTAMP_KEY, timestamp.toString());

    console.log('✅ FCM token generated and stored successfully');
    console.log('🔑 Token:', token.substring(0, 50) + '...');

    // Clear retry tracking on successful token generation
    clearPermissionAttemptInfo();

    return {
      success: true,
      token,
      permissionGranted: true,
      retryAttempts: 0, // Reset since we succeeded
    };
  } catch (error) {
    console.error('🔥 FCM token generation failed:', error);

    const currentAttemptInfo = getPermissionAttemptInfo();

    // ================================================================
    // ERROR CATEGORIZATION WITH RETRY INFO
    // ================================================================
    //
    // Purpose: Provide specific error messages for different failures
    // UX: Help users understand and resolve permission issues
    // Debugging: Detailed error information for development
    // ================================================================

    let errorMessage = 'Unknown error occurred';

    if (error instanceof Error) {
      if (error.message.includes('permission')) {
        errorMessage = 'Notification permission required';
      } else if (error.message.includes('vapid')) {
        errorMessage = 'VAPID key configuration error';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error during token generation';
      } else {
        errorMessage = error.message;
      }
    }

    return {
      success: false,
      error: errorMessage,
      retryAttempts: currentAttemptInfo.attemptCount,
      retryLimitExceeded: currentAttemptInfo.attemptCount >= FCM_RETRY_CONFIG.maxRetryAttempts,
    };
  }
}

/**
 * Get stored FCM token with validity check
 *
 * @returns {FCMTokenInfo | null} Token information or null if not available
 */
export function getStoredFCMToken(): FCMTokenInfo | null {
  try {
    // ================================================================
    // TOKEN RETRIEVAL
    // ================================================================
    //
    // Purpose: Retrieve stored token with metadata
    // Validation: Check token format and age
    // Cleanup: Remove invalid or expired tokens
    // ================================================================

    const token = sessionStorage.getItem(FCM_TOKEN_KEY);
    const timestampStr = sessionStorage.getItem(FCM_TOKEN_TIMESTAMP_KEY);

    if (!token || !timestampStr) {
      return null;
    }

    const timestamp = parseInt(timestampStr, 10);
    const currentTime = Date.now();
    const tokenAge = currentTime - timestamp;

    // Check if token is expired (older than refresh interval)
    const isValid = tokenAge < TOKEN_REFRESH_INTERVAL;

    return {
      token,
      timestamp,
      isValid,
    };
  } catch (error) {
    console.error('🔑 Error retrieving stored FCM token:', error);
    return null;
  }
}

/**
 * Get valid FCM token (generate new if needed)
 *
 * @returns {Promise<string | null>} Valid FCM token or null
 * @async
 */
export async function getValidFCMToken(): Promise<string | null> {
  try {
    // ================================================================
    // TOKEN VALIDATION LOGIC
    // ================================================================
    //
    // Purpose: Ensure we always have a valid, fresh token
    // Strategy: Use stored token if valid, generate new if expired
    // Performance: Avoid unnecessary token generation
    // ================================================================

    const storedTokenInfo = getStoredFCMToken();

    // If we have a valid stored token, use it
    if (storedTokenInfo && storedTokenInfo.isValid) {
      console.log('🔑 Using valid stored FCM token');
      return storedTokenInfo.token;
    }

    // If stored token is expired or missing, generate new one
    if (storedTokenInfo && !storedTokenInfo.isValid) {
      console.log('🔄 Stored FCM token expired, generating new token');
    } else {
      console.log('🔑 No stored FCM token found, generating new token');
    }

    const result = await generateFCMToken();

    if (result.success && result.token) {
      return result.token;
    }

    console.error('❌ Failed to get valid FCM token:', result.error);
    return null;
  } catch (error) {
    console.error('🔑 Error getting valid FCM token:', error);
    return null;
  }
}

/**
 * Refresh FCM token if needed
 *
 * @returns {Promise<FCMTokenResult>} Token refresh result
 * @async
 */
export async function refreshFCMTokenIfNeeded(): Promise<FCMTokenResult> {
  try {
    const storedTokenInfo = getStoredFCMToken();

    // If token is still valid, no refresh needed
    if (storedTokenInfo && storedTokenInfo.isValid) {
      return {
        success: true,
        token: storedTokenInfo.token,
      };
    }

    // Token is expired or missing, generate new one
    console.log('🔄 Refreshing FCM token...');
    return await generateFCMToken();
  } catch (error) {
    console.error('🔄 Error refreshing FCM token:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear stored FCM token
 *
 * @returns {void}
 */
export function clearFCMToken(): void {
  try {
    sessionStorage.removeItem(FCM_TOKEN_KEY);
    sessionStorage.removeItem(FCM_TOKEN_TIMESTAMP_KEY);
    console.log('🗑️ FCM token cleared from storage');
  } catch (error) {
    console.error('🗑️ Error clearing FCM token:', error);
  }
}

// ================================================================
// MESSAGE HANDLING
// ================================================================

/**
 * Set up foreground message handler
 *
 * @param {Function} onMessageReceived - Callback for received messages
 * @returns {Promise<void>}
 * @async
 */
export async function setupForegroundMessageHandler(
  onMessageReceived: (payload: any) => void,
): Promise<void> {
  try {
    // ================================================================
    // FOREGROUND MESSAGE SETUP
    // ================================================================
    //
    // Purpose: Handle messages when app is in foreground
    // Integration: Connect with existing notification system
    // UX: Show notifications that integrate with app UI
    // ================================================================

    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      console.warn('🔥 Firebase messaging not available for foreground handler');
      return;
    }

    onMessage(messaging, (payload) => {
      console.log('📨 Foreground message received:', payload);

      // ================================================================
      // BROWSER NOTIFICATION DISPLAY
      // ================================================================
      //
      // Purpose: Show browser notification popup for foreground messages
      // Permissions: Check notification permission before showing
      // Fallback: Call callback if notifications not available
      // ================================================================

      if ('Notification' in window && Notification.permission === 'granted') {
        const notificationTitle = payload.notification?.title || 'AP Backoffice';
        const notificationOptions = {
          body: payload.notification?.body || 'You have a new notification',
          icon: payload.notification?.icon || '/vite.svg',
          image: payload.notification?.image,
          tag: payload.data?.tag || 'ap-backoffice-foreground',
          data: {
            url: payload.data?.url || '/dashboard',
            notificationId: payload.data?.notificationId,
            timestamp: Date.now(),
            source: 'foreground',
            ...payload.data,
          },
          requireInteraction: false,
          silent: false,
        };

        // Show browser notification
        const notification = new Notification(notificationTitle, notificationOptions);

        // Handle notification click
        notification.onclick = function (event) {
          console.log('🔔 Foreground notification clicked');
          event.preventDefault();

          // Close notification
          notification.close();

          // Focus window
          if (window) {
            window.focus();

            // Navigate to URL if provided
            const targetUrl = notificationOptions.data?.url;
            if (targetUrl && targetUrl !== '/dashboard' && window.location) {
              window.location.href = targetUrl;
            }
          }
        };

        // Auto-close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);

        console.log('🔔 Foreground notification displayed');
      } else {
        console.warn('⚠️ Notifications not permitted, using callback only');
      }

      // Call the provided callback with the message payload
      onMessageReceived(payload);
    });

    console.log('✅ Foreground message handler set up successfully');
  } catch (error) {
    console.error('📨 Error setting up foreground message handler:', error);
  }
}

/**
 * ================================================================
 * END OF FILE: FCM Token Manager
 * ================================================================
 *
 * @exports          FCM token management functions
 * @dependencies     Firebase messaging, session storage
 * @usedBy           Auth service, notification hooks, app initialization
 *
 * @maintenance
 * - Token refresh: Monitor token refresh intervals
 * - Permissions: Handle permission changes gracefully
 * - Storage: Consider migration to IndexedDB for persistence
 *
 * @security
 * - Token storage: Session storage for security
 * - Permissions: Always check before token operations
 * - Validation: Verify token format and expiration
 *
 * ================================================================
 */
